import React, { useCallback, useRef, useState } from "react";
import { useModS } from "../../hooks/useModS";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import EmployeeList from "../EmployeeList";
import type { EmployeeListHandle } from "../EmployeeList";
import Container from "../Container";
import Page from "../Page";

const EmployeesPage: React.FunctionComponent = () => {
  const [includedCount, setIncludedCount] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const employeeListRef = useRef<EmployeeListHandle>(null);

  const handleSave = useCallback(async () => {
    setSaving(true);

    try {
      await employeeListRef.current?.save();
      toast.success("Employees updated");
      setDirty(false);
    } catch (_err) {
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  }, []);

  const handleDirtyChange = useCallback((isDirty: boolean) => {
    setDirty(isDirty);
  }, []);

  useModS(handleSave, !saving && dirty);

  return (
    <Page>
      <Container size="wide">
        <div className="mb-6">
          <Heading level={1} variant="lg">Employees</Heading>
          <Text variant="xl">
            Choose which employees to include in your daily scores.
          </Text>
        </div>

        <EmployeeList
          ref={employeeListRef}
          onIncludedCountChange={setIncludedCount}
          onDirtyChange={handleDirtyChange}
        />

        <div className="flex items-center justify-between gap-4 mt-6">
          <Text variant="sm" className="text-muted-foreground">
            {includedCount} {includedCount === 1 ? "employee" : "employees"} will receive daily scores.
          </Text>
          <Button onClick={handleSave} disabled={saving || !dirty}>
            {saving ? "Saving..." : dirty ? "Save Changes" : "Saved"}
          </Button>
        </div>
      </Container>
    </Page>
  );
};

export default EmployeesPage;
